import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useBlockSlot,
  useContentToken,
  useSlotGroup,
  useCollectionBinding,
  usePropertyAddressUrl,
  usePropertyField,
  SectionContextProvider,
  EditableToken,
  EditableSlotGroup,
  useEditMode,
} from '@vibesites/runtime/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFacebookF, faInstagram, faTiktok } from '@fortawesome/free-brands-svg-icons';

// ============================================================================
// Local helpers
// ============================================================================

/**
 * Remove a trailing country segment (USA / U.S.A. / United States [of America])
 * plus any leading comma/whitespace. Keeps the visible address focused on the
 * street/city/state on the marketing page.
 */
function stripCountry(raw: string | null | undefined): string {
  if (!raw) return '';
  return raw
    .replace(/\s*,?\s*(u\.?\s*s\.?\s*a\.?|united\s+states(\s+of\s+america)?)\s*$/i, '')
    .trim();
}

interface OfficeHourEntry {
  day?: string | null;
  open_time?: string | null;
  close_time?: string | null;
}

interface CondensedHourRow {
  dayLabel: string;
  timeLabel: string;
}

function formatTime(raw: string | null | undefined): string {
  if (!raw) return '';
  const trimmed = raw.trim();
  if (/am|pm/i.test(trimmed)) return trimmed;
  const [h, m] = trimmed.split(':').map(Number);
  if (isNaN(h)) return trimmed;
  const suffix = h >= 12 ? 'PM' : 'AM';
  const hr = h % 12 || 12;
  return m ? `${hr}:${String(m).padStart(2, '0')} ${suffix}` : `${hr} ${suffix}`;
}

/**
 * Collapses consecutive days that share the same open/close times into a
 * single range row, e.g. Mon–Fri: 9 AM – 6 PM.
 */
function condenseOfficeHours(hours: OfficeHourEntry[]): CondensedHourRow[] {
  const rows: CondensedHourRow[] = [];
  let i = 0;
  while (i < hours.length) {
    const cur = hours[i];
    const timeKey = `${cur.open_time ?? ''}|${cur.close_time ?? ''}`;
    let j = i + 1;
    while (j < hours.length) {
      const nxt = hours[j];
      if (`${nxt.open_time ?? ''}|${nxt.close_time ?? ''}` !== timeKey) break;
      j++;
    }
    const span = j - i;
    const dayLabel =
      span === 1
        ? (cur.day ?? '')
        : `${cur.day ?? ''} – ${hours[j - 1].day ?? ''}`;
    const timeLabel =
      cur.open_time && cur.close_time
        ? `${formatTime(cur.open_time)} – ${formatTime(cur.close_time)}`
        : 'Closed';
    rows.push({ dayLabel, timeLabel });
    i = j;
  }
  return rows;
}

const DAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
const DAY_LABELS: Record<string, string> = {
  mon: 'Monday',
  tue: 'Tuesday',
  wed: 'Wednesday',
  thu: 'Thursday',
  fri: 'Friday',
  sat: 'Saturday',
  sun: 'Sunday',
};

function normalizeDayKey(day: string | null | undefined): string {
  return (day ?? '').trim().toLowerCase().slice(0, 3);
}

/**
 * Given the raw office_hours collection, return an entry for every day of the
 * week — filling any missing / partially-blank days with a "closed" placeholder
 * so `condenseOfficeHours` renders them as "Closed" rows.
 */
function padWithClosedDays(hours: OfficeHourEntry[]): OfficeHourEntry[] {
  const byKey = new Map<string, OfficeHourEntry>();
  for (const entry of hours) {
    const key = normalizeDayKey(entry.day);
    if (key && DAY_ORDER.includes(key as (typeof DAY_ORDER)[number]) && !byKey.has(key)) {
      byKey.set(key, entry);
    }
  }
  return DAY_ORDER.map((key) => {
    const existing = byKey.get(key);
    return {
      day: DAY_LABELS[key],
      open_time: existing?.open_time ?? null,
      close_time: existing?.close_time ?? null,
    };
  });
}

// ============================================================================
// Schema definitions (module-level literals — extractor follows one level)
// ============================================================================

const addressLinkDef: SlotGroupDefinition = {
  label: 'Address',
  fields: {
    text: {
      label: 'Address',
      type: 'text',
      defaultField: 'property_full_address',
      defaultValue: '',
    },
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_google_place_url',
      suggestedFields: ['property_google_place_url'],
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const facebookLinkDef: SlotGroupDefinition = {
  label: 'Facebook',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_facebook_url',
      suggestedFields: ['property_facebook_url'],
    },
    label: { label: 'Label', type: 'text', defaultValue: 'Facebook' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const instagramLinkDef: SlotGroupDefinition = {
  label: 'Instagram',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_instagram_url',
      suggestedFields: ['property_instagram_url'],
    },
    label: { label: 'Label', type: 'text', defaultValue: 'Instagram' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const tiktokLinkDef: SlotGroupDefinition = {
  label: 'TikTok',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_tiktok_url',
      suggestedFields: ['property_tiktok_url'],
    },
    label: { label: 'Label', type: 'text', defaultValue: 'TikTok' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

// ============================================================================
// Component
// ============================================================================

export function ScheduleTourForm() {
  const addressLabel = useContentToken('scheduletourform.address_label');
  const hoursLabel = useContentToken('scheduletourform.hours_label');

  const addressLink = useSlotGroup('scheduletourform.address', addressLinkDef);
  const facebook = useSlotGroup('scheduletourform.facebook_link', facebookLinkDef);
  const instagram = useSlotGroup('scheduletourform.instagram_link', instagramLinkDef);
  const tiktok = useSlotGroup('scheduletourform.tiktok_link', tiktokLinkDef);
  const isEditMode = Boolean(useEditMode()?.isEditMode);

  const propertyAddressUrl = usePropertyAddressUrl();
  const propertyAddress = usePropertyField('property_full_address') as
    | string
    | null
    | undefined;

  const officeHours = useCollectionBinding<OfficeHourEntry>(
    'scheduletourform.office_hours',
    { collection: 'office_hours' },
  );

  const formSlot = useBlockSlot('scheduletourform.form_block', {
    defaultValue: 'multihub-tour-scheduler',
  });

  const addressText = stripCountry(addressLink.text || propertyAddress || '');
  const addressHref = addressLink.url || propertyAddressUrl || '#';
  const hoursRows =
    officeHours.length > 0 ? condenseOfficeHours(padWithClosedDays(officeHours)) : [];
  const hasSocial = Boolean(facebook.url || instagram.url || tiktok.url);

  return (
    <section className="stf-section">
      <div className="stf-container">
        <div className="stf-grid">
          {/* Left column: Address, Hours, Socials */}
          <aside className="stf-info-col">
            <EditableToken
              path="scheduletourform.address_label"
              as="h3"
              className="stf-info-heading"
            >
              {addressLabel || 'Address'}
            </EditableToken>
            <EditableSlotGroup
              groupPath="scheduletourform.address"
              definition={addressLinkDef}
            >
              {addressLink.url || propertyAddressUrl ? (
                <a
                  href={addressHref}
                  target={addressLink._target || '_blank'}
                  rel="noopener noreferrer"
                  className="stf-info-text stf-info-link"
                >
                  {addressText}
                </a>
              ) : (
                <span className="stf-info-text">{addressText}</span>
              )}
            </EditableSlotGroup>

            {hoursRows.length > 0 && (
              <>
                <EditableToken
                  path="scheduletourform.hours_label"
                  as="h3"
                  className="stf-info-heading stf-info-heading--spaced"
                >
                  {hoursLabel || 'Hours'}
                </EditableToken>
                <div className="stf-hours">
                  {hoursRows.map((row, i) => (
                    <div key={i} className="stf-hours-row">
                      <span className="stf-hours-day">{row.dayLabel}</span>
                      <span className="stf-hours-time">{row.timeLabel}</span>
                    </div>
                  ))}
                </div>
              </>
            )}

            <div className={`stf-socials${hasSocial ? '' : ' stf-socials--empty'}`}>
              <EditableSlotGroup
                groupPath="scheduletourform.facebook_link"
                definition={facebookLinkDef}
              >
                {facebook.url ? (
                  <a
                    href={facebook.url}
                    target={facebook._target || '_blank'}
                    rel="noopener noreferrer"
                    aria-label={facebook.label || 'Facebook'}
                    className="stf-social-link"
                  >
                    <FontAwesomeIcon icon={faFacebookF} />
                  </a>
                ) : (
                  <span className="stf-social-link stf-social-link--placeholder" aria-hidden="true">
                    <FontAwesomeIcon icon={faFacebookF} />
                  </span>
                )}
              </EditableSlotGroup>
              <EditableSlotGroup
                groupPath="scheduletourform.instagram_link"
                definition={instagramLinkDef}
              >
                {instagram.url ? (
                  <a
                    href={instagram.url}
                    target={instagram._target || '_blank'}
                    rel="noopener noreferrer"
                    aria-label={instagram.label || 'Instagram'}
                    className="stf-social-link"
                  >
                    <FontAwesomeIcon icon={faInstagram} />
                  </a>
                ) : (
                  <span className="stf-social-link stf-social-link--placeholder" aria-hidden="true">
                    <FontAwesomeIcon icon={faInstagram} />
                  </span>
                )}
              </EditableSlotGroup>
              {tiktok.url || isEditMode ? (
                <EditableSlotGroup
                  groupPath="scheduletourform.tiktok_link"
                  definition={tiktokLinkDef}
                >
                  {tiktok.url ? (
                    <a
                      href={tiktok.url}
                      target={tiktok._target || '_blank'}
                      rel="noopener noreferrer"
                      aria-label={tiktok.label || 'TikTok'}
                      className="stf-social-link"
                    >
                      <FontAwesomeIcon icon={faTiktok} />
                    </a>
                  ) : (
                    <span className="stf-social-link stf-social-link--placeholder" aria-hidden="true">
                      <FontAwesomeIcon icon={faTiktok} />
                    </span>
                  )}
                </EditableSlotGroup>
              ) : null}
            </div>
          </aside>

          {/* Right column: form */}
          <div className="stf-form-col">
            <EditableToken
              path="scheduletourform.form_block"
              as="div"
              className="stf-form-wrap"
            >
              {formSlot ? (
                <SectionContextProvider sectionId={formSlot.sectionId}>
                  <formSlot.BlockComponent sectionId={formSlot.sectionId} />
                </SectionContextProvider>
              ) : (
                <p className="stf-form-empty">No form block selected</p>
              )}
            </EditableToken>
          </div>
        </div>
      </div>

      <style>{`
        .stf-section {
          background: white;
          padding: 3rem 1.5rem;
        }
        @media (min-width: 768px) {
          .stf-section { padding: 5rem 2rem; }
        }
        .stf-container {
          max-width: 76rem;
          margin: 0 auto;
        }
        .stf-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 2.5rem;
        }
        @media (min-width: 900px) {
          .stf-grid {
            grid-template-columns: minmax(220px, 1fr) minmax(0, 2fr);
            gap: 4rem;
            align-items: start;
          }
        }

        /* ── Info column ── */
        .stf-info-col {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
        }
        @media (min-width: 900px) {
          .stf-info-col {
            align-items: flex-start;
            text-align: left;
          }
        }
        .stf-info-heading {
          font-family: var(--vibe-font-heading);
          font-size: 1.75rem;
          font-weight: 700;
          color: var(--vibe-color-primary);
          line-height: 1.2;
          margin: 0 0 1rem;
        }
        .stf-info-heading--spaced {
          margin-top: 2.25rem;
        }
        /* Match HeroSolidBG blurb: body font, text-base / md:text-lg, leading-relaxed */
        .stf-info-text {
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          font-weight: 400;
          line-height: 1.625;
          color: var(--vibe-color-text);
          text-decoration: none;
        }
        @media (min-width: 768px) {
          .stf-info-text { font-size: 1.125rem; }
        }
        .stf-info-link {
          transition: color 0.2s ease;
        }
        .stf-info-link:hover,
        .stf-info-link:focus-visible {
          color: var(--vibe-color-primary);
          text-decoration: underline;
        }

        /* ── Hours list ── */
        .stf-hours {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.5rem;
        }
        @media (min-width: 900px) {
          .stf-hours {
            align-items: flex-start;
          }
        }
        .stf-hours-row {
          display: flex;
          align-items: baseline;
          gap: 2rem;
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          font-weight: 400;
          line-height: 1.625;
          color: var(--vibe-color-text);
        }
        @media (min-width: 768px) {
          .stf-hours-row { font-size: 1.125rem; }
        }
        .stf-hours-day {
          min-width: 6rem;
          color: var(--vibe-color-text);
        }
        .stf-hours-time {
          color: var(--vibe-color-text);
        }

        /* ── Social row ── */
        .stf-socials {
          margin-top: 2rem;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 1.25rem;
        }
        @media (min-width: 900px) {
          .stf-socials {
            justify-content: flex-start;
          }
        }
        .stf-socials--empty {
          /* keep the row present (both wrappers must always render for the editor)
             but out of the way when neither URL is set */
          margin-top: 1rem;
          opacity: 0.35;
        }
        .stf-social-link {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          color: var(--vibe-color-primary);
          font-size: 1.5rem;
          line-height: 1;
          transition: opacity 0.2s ease, transform 0.2s ease;
          text-decoration: none;
        }
        .stf-social-link:hover,
        .stf-social-link:focus-visible {
          opacity: 0.75;
          transform: translateY(-1px);
        }
        .stf-social-link--placeholder {
          cursor: default;
        }

        /* ── Form column ── */
        .stf-form-wrap {
          width: 100%;
        }
        .stf-form-empty {
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          color: var(--vibe-color-text-muted);
          margin: 0;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ScheduleTourForm',
  displayName: 'Schedule A Tour Form',
  tokens: {
    'scheduletourform.form_block': {
      label: 'Form Block',
      type: 'block',
      defaultValue: 'multihub-tour-scheduler',
      acceptedBlockCategories: ['Forms'],
    },
    'scheduletourform.address_label': {
      label: 'Address Heading',
      type: 'text',
      defaultValue: 'Address',
    },
    'scheduletourform.hours_label': {
      label: 'Hours Heading',
      type: 'text',
      defaultValue: 'Hours',
    },
  },
  slotGroups: {
    'scheduletourform.address': addressLinkDef,
    'scheduletourform.facebook_link': facebookLinkDef,
    'scheduletourform.instagram_link': instagramLinkDef,
    'scheduletourform.tiktok_link': tiktokLinkDef,
  },
  collectionBindings: {
    'scheduletourform.office_hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
  },
};

(ScheduleTourForm as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default ScheduleTourForm;
